import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
  styled,
  TextField,
} from "@mui/material";
import { useVaraVerificationDetailsMutation } from "../redux/api/authApi";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { authStorageGet } from "../utils/authStorage";

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#F3F4F6",
  padding: theme.spacing(3),
  boxSizing: "border-box",
}));

const MainCard = styled(Card)(() => ({
  width: "100%",
  maxWidth: 575,
  height: "88vh",
  borderRadius: 18,
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  boxShadow: "0 8px 24px rgba(16, 24, 40, 0.08), 0 2px 6px rgba(16, 24, 40, 0.04)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const ScrollContent = styled(Box)(() => ({
  flex: 1,
  height: "100%",
  overflowY: "auto",
  padding: "22px 20px 16px",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#D1D5DB",
    borderRadius: 20,
  },
}));

const LogoCircle = styled(Box)(() => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 14px",
  boxShadow: "0 4px 16px rgba(117, 63, 255, 0.14)",
  border: "1px solid #E9DDFE",
}));

const VaraLogo = styled(Box)(() => ({
  width: 28,
  height: 28,
  borderRadius: 6,
  background: "linear-gradient(135deg, #190B46 0%, #4B1FA1 48%, #FF8A3D 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontWeight: 700,
  fontSize: 8,
  letterSpacing: "0.6px",
}));

const SubmitButton = styled(Button)(() => ({
  width: "100%",
  height: 44,
  borderRadius: 10,
  textTransform: "none",
  fontSize: 15,
  fontWeight: 700,
  color: "#FFFFFF",
  background: "linear-gradient(90deg, #8A1CF2 0%, #A61EFF 55%, #8A1CF2 100%)",
  boxShadow: "none",
  "&:hover": {
    background: "linear-gradient(90deg, #7A17D9 0%, #9719E8 55%, #7A17D9 100%)",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#FFFFFF",
    opacity: 0.7,
  },
}));

// const BackButton = styled(Button)(() => ({
//   width: "100%",
//   height: 40,
//   borderRadius: 10,
//   textTransform: "none",
//   fontSize: 14,
//   fontWeight: 600,
//   color: "#101828",
//   background: "#FFFFFF",
//   border: "1px solid #E5E7EB",
//   boxShadow: "none",
//   "&:hover": {
//     background: "#F9FAFB",
//     boxShadow: "none",
//   },
// }));

const UploadBox = styled(Box)(() => ({
  border: "1px dashed #CBD5E1",
  borderRadius: 10,
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
  color: "#475467",
  background: "#FFFFFF",
  padding: "0 12px",
}));

// const InfoNote = styled(Box)(() => ({
//   border: "1px solid #E9D5FF",
//   background: "#FAF5FF",
//   borderRadius: 12,
//   padding: "12px 14px",
// }));

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    background: "#FFFFFF",
  },
}));

const RequiredLabel = ({ text }) => {
  const parts = text.split(" *");
  return (
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#101828", mb: 1 }}>
      {parts[0]}
      {parts.length > 1 && <span style={{ color: "#ef4444" }}> *</span>}
      {parts.slice(1).join(" *").replace(/^ /, "")}
    </Typography>
  );
};

const InputField = ({
  label,
  value,
  onChange,
  name,
  placeholder,
  required = false,
  error,
}) => (
  <Box>
    <RequiredLabel text={label} />
    <StyledTextField
      fullWidth
      size="small"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      error={!!error}
      helperText={error}
    />
  </Box>
);

const DocumentUpload = ({
  title,
  uploadText,
  helperText,
  inputId,
  fileName,
  onChange,
  error,
}) => (
  <Box>
    <RequiredLabel text={title} />

    <label htmlFor={inputId} style={{ display: "block" }}>
      <input id={inputId} type="file" hidden onChange={onChange} />
      <UploadBox sx={error ? { borderColor: "#ef4444" } : {}}>
        <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: error ? "#ef4444" : "#667085" }} />
        <Typography
          sx={{
            fontSize: 14,
            color: fileName ? "#101828" : error ? "#ef4444" : "#475467",
            lineHeight: "20px",
          }}
        >
          {fileName || uploadText}
        </Typography>
      </UploadBox>
    </label>

    {error ? (
      <Typography sx={{ mt: 0.5, fontSize: 12, color: "#ef4444", lineHeight: "18px" }}>
        {error}
      </Typography>
    ) : (
      <Typography sx={{ mt: 0.5, fontSize: 12, color: "#667085", lineHeight: "18px" }}>
        {helperText} · Max 10 MB
      </Typography>
    )}
  </Box>
);

const VaraInformation = ({ onBack, onSubmitSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userId =
    location.state?.userId ?? authStorageGet("signupUserId") ?? undefined;
  const role =
    location.state?.role ?? authStorageGet("signupRole") ?? undefined;

  const [submitVaraVerificationDetails, { isLoading }] =
    useVaraVerificationDetailsMutation();

  const [formValues, setFormValues] = useState({
    passportNumber: "",
    cryptoWalletAddress: "",
    sourceOfFunds: "",
    tradingExperience: "",
  });

  const [files, setFiles] = useState({
    passportDoc: null,
    proofOfFundsDoc: null,
    proofOfAddressDoc: null,
  });

  const [fileErrors, setFileErrors] = useState({
    passportDoc: "",
    proofOfFundsDoc: "",
    proofOfAddressDoc: "",
  });

  const [fieldErrors, setFieldErrors] = useState({
    passportNumber: "",
    cryptoWalletAddress: "",
    sourceOfFunds: "",
  });

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (fieldName) => (event) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
      setFileErrors((prev) => ({ ...prev, [fieldName]: "File size must not exceed 10 MB." }));
      event.target.value = "";
      return;
    }
    setFiles((prev) => ({ ...prev, [fieldName]: selectedFile }));
    setFileErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const handleSubmit = async () => {
    const newFieldErrors = {};
    if (!formValues.passportNumber.trim()) newFieldErrors.passportNumber = "Passport / National ID number is required.";
    if (!formValues.cryptoWalletAddress.trim()) newFieldErrors.cryptoWalletAddress = "Crypto wallet address is required.";
    if (!formValues.sourceOfFunds.trim()) newFieldErrors.sourceOfFunds = "Source of funds is required.";

    const newFileErrors = { ...fileErrors };
    if (!files.passportDoc) newFileErrors.passportDoc = "Please upload your passport / ID copy.";
    if (!files.proofOfFundsDoc) newFileErrors.proofOfFundsDoc = "Please upload proof of funds.";
    if (!files.proofOfAddressDoc) newFileErrors.proofOfAddressDoc = "Please upload proof of address.";

    setFieldErrors(newFieldErrors);
    setFileErrors(newFileErrors);
    if (Object.values(newFieldErrors).some(Boolean) || Object.values(newFileErrors).some(Boolean)) return;

    try {
      const formData = new FormData();

      if (userId) {
        formData.append("userId", userId);
      }
      if (role) {
        formData.append("role", role);
      }

      formData.append("passportNumber", formValues.passportNumber);
      formData.append("cryptoWalletAddress", formValues.cryptoWalletAddress);
      formData.append("sourceOfFunds", formValues.sourceOfFunds);
      formData.append("tradingExperience", formValues.tradingExperience);

      if (files.passportDoc) {
        formData.append("passportDoc", files.passportDoc);
      }

      if (files.proofOfFundsDoc) {
        formData.append("proofOfFundsDoc", files.proofOfFundsDoc);
      }

      if (files.proofOfAddressDoc) {
        formData.append("proofOfAddressDoc", files.proofOfAddressDoc);
      }

      await submitVaraVerificationDetails(formData).unwrap();

      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        navigate("/vara-verification-request-sent");
      }
    } catch (error) {
      console.error("VARA verification submission failed:", error);
    }
  };

  return (
    <PageWrapper>
      <MainCard>
        <ScrollContent>
          <LogoCircle>
            <VaraLogo>VARA</VaraLogo>
          </LogoCircle>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 16,
              fontWeight: 700,
              color: "#101828",
              mb: 1,
            }}
          >
            Compliance Information
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 14,
              color: "#5B7093",
              lineHeight: "22px",
              mb: 4,
            }}
          >
            Please provide the required information for VARA would be verified by Admin
          </Typography>

          <Stack spacing={3}>
            <InputField
              label="Passport / National ID Number *"
              name="passportNumber"
              value={formValues.passportNumber}
              onChange={handleInputChange}
              placeholder="Enter passport / national ID number"
              required
              error={fieldErrors.passportNumber}
            />

            <InputField
              label="Crypto Wallet Address *"
              name="cryptoWalletAddress"
              value={formValues.cryptoWalletAddress}
              onChange={handleInputChange}
              placeholder="Enter crypto wallet address"
              required
              error={fieldErrors.cryptoWalletAddress}
            />

            <InputField
              label="Source of Funds *"
              name="sourceOfFunds"
              value={formValues.sourceOfFunds}
              onChange={handleInputChange}
              placeholder="Enter source of funds"
              required
              error={fieldErrors.sourceOfFunds}
            />

            <InputField
              label="Trading Experience (Optional)"
              name="tradingExperience"
              value={formValues.tradingExperience}
              onChange={handleInputChange}
              placeholder="Enter trading experience"
            />

            <Divider sx={{ borderColor: "#E5E7EB" }} />

            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#101828",
                  mb: 2,
                }}
              >
                Required Documents
              </Typography>

              <Stack spacing={2}>
                <DocumentUpload
                  inputId="passport-id-copy"
                  title="Passport / ID Copy *"
                  uploadText="Click to upload passport / id copy"
                  helperText="Clear copy of your ID photo page"
                  fileName={files.passportDoc?.name}
                  onChange={handleFileChange("passportDoc")}
                  error={fileErrors.passportDoc}
                />

                <DocumentUpload
                  inputId="proof-of-funds"
                  title="Proof of Funds *"
                  uploadText="Click to upload proof of funds"
                  helperText="Bank statements or investment account statements"
                  fileName={files.proofOfFundsDoc?.name}
                  onChange={handleFileChange("proofOfFundsDoc")}
                  error={fileErrors.proofOfFundsDoc}
                />

                <DocumentUpload
                  inputId="proof-of-address"
                  title="Proof of Address *"
                  uploadText="Click to upload proof of address"
                  helperText="Utility bill or bank statement dated within last 3 months"
                  fileName={files.proofOfAddressDoc?.name}
                  onChange={handleFileChange("proofOfAddressDoc")}
                  error={fileErrors.proofOfAddressDoc}
                />
              </Stack>
            </Box>

            {/* <InfoNote>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#9333EA",
                    mt: "2px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#7E22CE",
                    lineHeight: "18px",
                  }}
                >
                  All information is securely transmitted in compliance with VARA
                  regulations. Submissions are subject to admin approval.
                </Typography>
              </Stack>
            </InfoNote> */}

            <SubmitButton onClick={handleSubmit} disabled={isLoading}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <span>{isLoading ? "Submitting..." : "Submit for Verification"}</span>
                {!isLoading && <ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
              </Stack>
            </SubmitButton>

            {/* <BackButton onClick={onBack}>Back</BackButton> */}
          </Stack>
        </ScrollContent>
      </MainCard>
    </PageWrapper>
  );
};

export default VaraInformation;